import type { FC, ComponentType, Dispatch, SetStateAction } from 'react';
import React, { Fragment, useState } from 'react';
import { Query } from 'react-apollo';
import type { QueryResult } from 'react-apollo';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { Inline, Flex, xcss, Box } from '@atlaskit/primitives';

import { CUSTOM_SITES_PAGE_TITLE_FF } from '@confluence/emoji-title/entry-points/constants';
import {
	VIEW_PAGE_CONTENT_HEADER_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { ContentTypesHeader } from '@confluence/content-types-header';
import {
	Breadcrumbs,
	FixedSpaceHeaderContainer,
	FixedSpaceHeaderWithBannerContainer,
} from '@confluence/breadcrumbs';
import {
	Attribution,
	ErrorBoundary,
	ErrorDisplay,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	CONTENT_METADATA_PAGE_RESTRICTIONS_ID,
	CONTENT_METADATA_ATTACHMENTS_ID,
} from '@confluence/web-item';
import { useBooleanFeatureFlag } from '@confluence/session-data';
import { RendererContentState } from '@confluence/content-state';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries';
import { usePageHeightWidthObserver } from '@confluence/page-cover-picture';
import { ConvertPageToFolderBannerLoader } from '@confluence/folder/entry-points/ConvertPageToFolderBannerLoader';
import { ViewPageDistinctionBanner } from '@confluence/live-pages-features';
import { fg } from '@confluence/feature-gating';
import { isLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/isLivePagesFeatureEnabled';
import { ContentHeaderPresenceLoader } from '@confluence/team-presence/entry-points/ContentHeaderPresenceLoader';

import { ContentHeaderUnifiedQuery } from './ContentHeaderUnifiedQuery.graphql';
import type { ContentHeaderUnifiedQuery as ContentHeaderUnifiedQueryType } from './__types__/ContentHeaderUnifiedQuery';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from './defaultQueryVariables';
import { PageMetadataAtl } from './PageMetadataAtl';
import { PageMetadataBanner } from './PageMetadataBanner';
import { SystemContentMetadata } from './SystemContentMetadata';
import { GroupedPageButtons } from './GroupedPageButtons';
import { CONNECT_APP_WEB_FRAGMENT_METRIC } from './perf.config';

const ContentClassificationTagLoader = LoadableAfterPaint({
	name: 'ContentClassificationTag',
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentClassificationTag" */ '@confluence/data-classification'
			)
		).ContentClassificationTag,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexBreadcrumbsSpacer = styled.div({
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpacedActionItem = styled.div({
	marginLeft: token('space.050', '4px'),
});

const BREADCRUMBS_MIN_WIDTH = '150px'; // needed for responsive breadcrumbs

const breadcrumbsContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	minWidth: BREADCRUMBS_MIN_WIDTH,
});

const breadcrumbsAreaWrapper = xcss({
	flexWrap: 'wrap',
	minWidth: BREADCRUMBS_MIN_WIDTH,
});

const modernizedBreadcrumbsAreaWrapperStyles = xcss({
	flexWrap: 'nowrap',
	flex: '1 1 auto',
	minWidth: '240px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const Metadata = styled.div((props: MetadataProps) => ({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	overflow: 'hidden', // needed to hide unpublished lozenge below certain header width
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: props.shouldDisplayClassification ? '20px' : '30px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .content-metadata-jira': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginTop: props.shouldDisplayClassification ? '-5px' : '0',
	},
}));

const verticalSeparatorStyles = xcss({
	borderLeft: `2px solid ${token('color.border')}`,
	height: '16px',
	margin: 'space.075',
});

const contentClassificationTagStyles = xcss({
	flexBasis: '100%',
});

const modernizedBreadcrumbsContentClassificationTagStyles = xcss({
	flexBasis: 'unset',
});

const VerticalSeparator = () => <Box xcss={verticalSeparatorStyles} />;

SpacedActionItem.displayName = 'SpacedActionItem';

const EXCLUDED_SYSTEM_IDS = [
	CONTENT_METADATA_PAGE_RESTRICTIONS_ID,
	CONTENT_METADATA_ATTACHMENTS_ID,
];

const SYSTEM_IDS_TO_SHOW_WITH_PAGE_BUTTONS = [CONTENT_METADATA_PAGE_RESTRICTIONS_ID];

type MetadataProps = {
	shouldDisplayClassification: boolean;
};

type RenderContentHeaderProps = {
	contentType: string | null | undefined;
	contentTitle: string | null | undefined;
	isFabricPage: boolean;
	loading: boolean;
	subType: string | null | undefined;
	lastModifiedDate: string | null | undefined;
	isThemed: boolean;
	isContentToolsForcedOpen: boolean;
	setIsContentToolsForcedOpen: Dispatch<SetStateAction<boolean>>;
};

type RenderBreadcrumbsProps = {
	contentType: string | null | undefined;
	contentTitle: string | null | undefined;
	contentId: string;
	isCustomSitesPageTitleFFOn: boolean;
};

type ContentHeaderProps = {
	spaceKey: string;
	contentId: string;
	actionItems?: ComponentType<any>[];
	versionOverride?: number;
	className?: string;
	showMetadataItems?: boolean;
};

export const ContentHeader: FC<ContentHeaderProps> = ({
	spaceKey,
	contentId,
	actionItems = [],
	versionOverride = null,
	showMetadataItems = true,
}) => {
	const isCustomSitesPageTitleFFOn = useBooleanFeatureFlag(CUSTOM_SITES_PAGE_TITLE_FF);

	const [isContentToolsForcedOpen, setIsContentToolsForcedOpen] = useState(false);

	const { isExternalCollaborator } = useIsExternalCollaborator();
	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: 'current-only',
	});

	const { shouldDisplayClassification } = contentClassificationMetadata;

	// These values are entirely empirical and expected to be a limited-time workaround to fix the wrapping issue for the classification tag.
	// Will be removed when https://product-fabric.atlassian.net/browse/NARU-1170 is worked on.
	const leftRightPaddingOffset = -37;
	const widthLimitForMetadata = 1250;

	const { width } = usePageHeightWidthObserver(
		isCustomSitesPageTitleFFOn,
		false,
		leftRightPaddingOffset,
		false,
	);

	const renderBreadcrumbs = ({
		contentType,
		contentTitle,
		contentId,
		isCustomSitesPageTitleFFOn,
	}: RenderBreadcrumbsProps) => {
		return (
			<Flex
				alignItems="center"
				xcss={[
					breadcrumbsAreaWrapper,
					fg('confluence_modernized_breadcrumbs') && modernizedBreadcrumbsAreaWrapperStyles,
				]}
				testId="breadcrumbs"
			>
				<Breadcrumbs contentId={contentId} spaceKey={spaceKey} contentTitle={contentTitle || ''} />
				<Inline alignBlock="center">
					{showMetadataItems &&
						(!shouldDisplayClassification ||
							(shouldDisplayClassification && width > widthLimitForMetadata)) && (
							<Metadata
								data-testid="metadata-wrapper"
								shouldDisplayClassification={shouldDisplayClassification}
							>
								<SystemContentMetadata
									key={`metadata-${contentId}`}
									contentId={contentId}
									versionOverride={versionOverride}
									excludeIds={EXCLUDED_SYSTEM_IDS}
								/>
								<PageMetadataBanner
									key={`banner-${contentId}`}
									contentId={contentId}
									versionOverride={versionOverride}
								/>
							</Metadata>
						)}
				</Inline>
				{isCustomSitesPageTitleFFOn && (
					<Box
						xcss={[
							contentClassificationTagStyles,
							fg('confluence_modernized_breadcrumbs') &&
								modernizedBreadcrumbsContentClassificationTagStyles,
						]}
						testId="content-classification-tag"
					>
						<ContentClassificationTagLoader
							contentId={contentId}
							contentType={contentType || null}
							customSitesTitleComponent
							contentClassificationMetadata={contentClassificationMetadata}
							isEditMode={false}
						/>
					</Box>
				)}
			</Flex>
		);
	};

	const renderContentHeader = ({
		contentType,
		contentTitle,
		isFabricPage,
		loading,
		subType,
		lastModifiedDate,
		isContentToolsForcedOpen,
		setIsContentToolsForcedOpen,
	}: RenderContentHeaderProps) => {
		return (
			<Fragment>
				{fg('confluence_modernized_breadcrumbs') ? (
					renderBreadcrumbs({
						contentType,
						contentTitle,
						contentId,
						isCustomSitesPageTitleFFOn,
					})
				) : (
					<Box xcss={breadcrumbsContainerStyles} testId="breadcrumb">
						{renderBreadcrumbs({
							contentType,
							contentTitle,
							contentId,
							isCustomSitesPageTitleFFOn,
						})}
					</Box>
				)}
				{!fg('confluence_modernized_breadcrumbs') && <FlexBreadcrumbsSpacer />}
				{showMetadataItems && <PageMetadataAtl contentId={contentId} />}

				<Fragment>
					{fg('confluence_team_presence_general_availability') && (
						<Flex alignItems="center">
							<ContentHeaderPresenceLoader contentId={contentId} contentType={contentType} />
							<VerticalSeparator />
						</Flex>
					)}
					<RendererContentState contentId={contentId} isPagePreview={false} />
					<GroupedPageButtons
						contentId={contentId}
						version={versionOverride}
						isFabricPage={isFabricPage}
						includeSystemIds={SYSTEM_IDS_TO_SHOW_WITH_PAGE_BUTTONS}
						spaceKey={spaceKey}
						contentType={contentType}
						contentSubType={subType}
						isExternalCollaborator={isExternalCollaborator}
						lastModifiedDate={lastModifiedDate}
						showEdit={!(isLivePagesFeatureEnabled() && subType === 'live')}
						isContentToolsForcedOpen={isContentToolsForcedOpen}
						setIsContentToolsForcedOpen={setIsContentToolsForcedOpen}
					/>
				</Fragment>

				{actionItems?.map((ActionItem: ComponentType<any>, index) => {
					const actionItemProps = {
						contentId,
						spaceKey,
						contentType,
					};
					return (
						<SpacedActionItem
							data-testid="spacedActionItemTestId"
							key={ActionItem?.displayName || index}
						>
							<ActionItem {...actionItemProps} disabled={loading} />
						</SpacedActionItem>
					);
				})}
			</Fragment>
		);
	};

	const renderContentTypesHeader = () => {
		return <ContentTypesHeader contentId={contentId} />;
	};

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			{showMetadataItems && (
				<PageSegmentLoadStart
					key={`PageSegmentLoadStart-connect-${contentId}`}
					metric={CONNECT_APP_WEB_FRAGMENT_METRIC}
				/>
			)}
			<ExperienceStart id={contentId} name={VIEW_PAGE_CONTENT_HEADER_EXPERIENCE} />
			<Query
				query={ContentHeaderUnifiedQuery}
				variables={{
					contentId,
					spaceKey,
					// Don't omit versionOverride, embeddedContentRender and limit
					// It's an Apollo bug that if preloader these needs to be match preloaded query
					// Also doesn't work if removing them from preloader variables HOT-109035
					versionOverride: versionOverride || null,
					embeddedContentRender: null,
					limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
				}}
				context={{ single: true }}
			>
				{({ loading, error, data }: QueryResult<ContentHeaderUnifiedQueryType>) => {
					if (error) {
						if (isUnauthorizedError(error)) {
							markErrorAsHandled(error);
							return null;
						} else {
							// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
							return <ErrorDisplay error={error} />;
						}
					} else if (!data) {
						return null;
					}

					const contentNode = data?.content?.nodes?.[0];
					const contentVersion = contentNode?.version?.number;
					const contentType = contentNode?.type;
					const lastModifiedDate = contentNode?.metadata?.lastModifiedDate;
					const subType = contentNode?.subType;
					const contentTitle = contentNode?.title;
					// property can be "v2" or v2
					const isFabricPage =
						contentNode?.properties?.nodes?.[0]?.value === '"v2"' ||
						contentNode?.properties?.nodes?.[0]?.value === 'v2';

					const spaceIsThemed = Boolean(data?.space?.theme?.themeKey);

					let viewPageDistinctionBanner: JSX.Element | null = null;
					let convertPageToFolderBanner: JSX.Element | null = null;

					if (fg('confluence_live_pages_ab_test_opted_in')) {
						viewPageDistinctionBanner = (
							<ViewPageDistinctionBanner
								contentId={contentId}
								spaceKey={spaceKey}
								versionNumber={contentVersion}
								waitForFolderBanner={fg('confluence_enable_page_to_folder_conversion')}
							/>
						);
					}

					if (fg('confluence_enable_page_to_folder_conversion')) {
						convertPageToFolderBanner = (
							<ConvertPageToFolderBannerLoader
								contentId={contentId}
								spaceKey={spaceKey}
								contentType={contentType}
								onActionButtonClick={() => setIsContentToolsForcedOpen(true)}
							/>
						);
					}

					const contentHeader = fg('confluence_frontend_object_header')
						? renderContentTypesHeader()
						: renderContentHeader({
								contentType,
								contentTitle,
								isFabricPage,
								loading,
								subType,
								lastModifiedDate,
								isThemed: spaceIsThemed,
								isContentToolsForcedOpen,
								setIsContentToolsForcedOpen,
							});

					if (viewPageDistinctionBanner || convertPageToFolderBanner) {
						const banners = (
							<>
								{viewPageDistinctionBanner}
								{convertPageToFolderBanner}
							</>
						);
						return (
							<FixedSpaceHeaderWithBannerContainer
								isThemed={spaceIsThemed}
								testId="content-header-container"
								banner={banners}
							>
								{contentHeader}
							</FixedSpaceHeaderWithBannerContainer>
						);
					}

					return (
						<FixedSpaceHeaderContainer
							isThemed={spaceIsThemed}
							data-testid="content-header-container"
						>
							{contentHeader}
						</FixedSpaceHeaderContainer>
					);
				}}
			</Query>
		</ErrorBoundary>
	);
};
